import { getAuthToken } from '../utils/authUtils'; //
import axios from 'axios';
import { store } from '../redux/store';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Get the user's default facility object
 * @param {Object} user - User object
 * @param {Array} userFacilities - Array of user's accessible facilities
 * @returns {Object|null} - Default facility object or null
 */
export const getDefaultFacility = (user, userFacilities) => {
  if (!user || !user.defaultFacility) return null;
  
  // defaultFacility is now the complete facility object
  // Return it directly if it has an ID (most common case)
  if (typeof user.defaultFacility === 'object' && user.defaultFacility.id) {
    return user.defaultFacility;
  }
  
  // Fallback: if defaultFacility is just an ID string, look it up in userFacilities
  const facilityId = user.defaultFacility;
  return userFacilities.find(f => f.id === facilityId) || null;
};


/**
 * Secure Care Coordination Service
 * Uses backend API endpoints instead of direct Firestore access
 */

// =============================================================================
// QUALITY METRICS
// =============================================================================

export async function getQualityMetrics(facilityId, program) {
  try {
    console.log('Getting quality metrics via API:', { facilityId, program });
    const token = await getAuthToken();
    
    // Get the user's default facility ID if not provided
    let targetFacilityId = facilityId;
    if (!targetFacilityId) {
      const state = store.getState();
      const user = state.auth.user;
      const userFacilities = state.facilities.userFacilities;
      const defaultFacility = getDefaultFacility(user, userFacilities);
      if (!defaultFacility || !defaultFacility.id) {
        throw new Error('No facility ID available. Please ensure you have access to a facility.');
      }
      targetFacilityId = defaultFacility.id;
    }
    
    const params = new URLSearchParams({ facilityId: targetFacilityId });
    if (program && program !== 'All') {
      params.append('program', program);
    }
    
    const response = await axios.get(
      `${API_BASE}/bi/quality-metrics?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.qualityMetrics || [];
  } catch (error) {
    console.error('Error getting quality metrics:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

// =============================================================================
// REFERRALS
// =============================================================================

export async function createReferral(data) {
  try {
    console.log('Creating referral via API:', data.patientId);
    const token = await getAuthToken();
    
    // Get the user's default facility ID if not provided
    let referralData = { ...data };
    if (!referralData.facilityId) {
      const state = store.getState();
      const user = state.auth.user;
      const userFacilities = state.facilities.userFacilities;
      const defaultFacility = getDefaultFacility(user, userFacilities);
      if (!defaultFacility || !defaultFacility.id) {
        throw new Error('No facility ID available. Please ensure you have access to a facility.');
      }
      referralData.facilityId = defaultFacility.id;
    }
    
    const response = await axios.post(
      `${API_BASE}/clinical/referrals`,
      referralData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      id: response.data.id,
      ...response.data
    };
  } catch (error) {
    console.error('Error creating referral:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

export async function getReferralsByPatient(patientId) {
  try {
    console.log('Getting referrals by patient via API:', patientId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/clinical/referrals/patient/${patientId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Handle both response formats for backward compatibility
    const referrals = response.data.referrals || response.data || [];
    
    // Ensure we always return an array
    if (!Array.isArray(referrals)) {
      console.warn('getReferralsByPatient: Expected array but got:', typeof referrals, referrals);
      return [];
    }
    
    return referrals;
  } catch (error) {
    console.error('Error getting referrals by patient:', error);
    throw error;
  }
}

export async function updateReferral(referralId, data) {
  try {
    console.log('Updating referral via API:', referralId);
    const token = await getAuthToken();
    
    await axios.put(
      `${API_BASE}/clinical/referrals/${referralId}`,
      data,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return { success: true };
  } catch (error) {
    console.error('Error updating referral:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

export async function deleteReferral(referralId) {
  try {
    console.log('Deleting referral via API:', referralId);
    const token = await getAuthToken();
    
    await axios.delete(
      `${API_BASE}/clinical/referrals/${referralId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting referral:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

export async function getAllReferrals(facilityId) {
  try {
    console.log('Getting all referrals via API for facility:', facilityId);
    
    // Get the user's default facility ID if not provided
    let targetFacilityId = facilityId;
    if (!targetFacilityId) {
      const state = store.getState();
      const user = state.auth.user;
      const userFacilities = state.facilities.userFacilities;
      const defaultFacility = getDefaultFacility(user, userFacilities);
      if (!defaultFacility || !defaultFacility.id) {
        throw new Error('No facility ID available. Please ensure you have access to a facility.');
      }
      targetFacilityId = defaultFacility.id;
    }
    
    // Get fresh token
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/clinical/referrals?facilityId=${targetFacilityId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Handle both response formats for backward compatibility
    const referrals = response.data.referrals || response.data || [];
    
    // Ensure we always return an array
    if (!Array.isArray(referrals)) {
      console.warn('getAllReferrals: Expected array but got:', typeof referrals, referrals);
      return [];
    }
    
    return referrals;
  } catch (error) {
    console.error('Error getting all referrals:', error);
    throw error;
  }
}

// =============================================================================
// REFERRAL ADMISSION WORKFLOW
// =============================================================================

export async function getAdmissionReadyReferrals(facilityId) {
  try {
    console.log('Getting admission-ready referrals via API:', facilityId);
    
    // Get the user's default facility ID if not provided
    let targetFacilityId = facilityId;
    if (!targetFacilityId) {
      const state = store.getState();
      const user = state.auth.user;
      const userFacilities = state.facilities.userFacilities;
      const defaultFacility = getDefaultFacility(user, userFacilities);
      if (!defaultFacility || !defaultFacility.id) {
        throw new Error('No facility ID available. Please ensure you have access to a facility.');
      }
      targetFacilityId = defaultFacility.id;
    }
    
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/clinical/referrals/admission-ready?facilityId=${targetFacilityId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Handle both response formats for backward compatibility
    const referrals = response.data.referrals || response.data || [];
    
    // Ensure we always return an array
    if (!Array.isArray(referrals)) {
      console.warn('getAdmissionReadyReferrals: Expected array but got:', typeof referrals, referrals);
      return [];
    }
    
    return referrals;
  } catch (error) {
    console.error('Error getting admission-ready referrals:', error);
    
    // If the admission-ready endpoint doesn't exist, fallback to getting all referrals and filtering
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.log('Admission-ready endpoint not found, falling back to getAllReferrals with filtering');
      try {
        const allReferrals = await getAllReferrals(facilityId);
        return allReferrals.filter(ref => 
          ref.status === 'ACTIVE' || ref.status === 'PENDING'
        );
      } catch (fallbackError) {
        console.error('Error in fallback for admission-ready referrals:', fallbackError);
        return [];
      }
    }
    
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

export async function admitReferral(referralId, patientData) {
  try {
    console.log('Admitting referral via API:', referralId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/clinical/referrals/${referralId}/admit`,
      patientData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error admitting referral:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

export async function getReferralForAdmissionForm(referralId) {
  try {
    console.log('Getting referral for admission form via API:', referralId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/clinical/referrals/${referralId}/admission-form`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Handle both response formats for backward compatibility
    const referral = response.data.referral || response.data || null;
    
    // Ensure we always return an object or null
    if (referral && typeof referral !== 'object') {
      console.warn('getReferralForAdmissionForm: Expected object but got:', typeof referral, referral);
      return null;
    }
    
    return referral;
  } catch (error) {
    console.error('Error getting referral for admission form:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

// =============================================================================
// CCDs (Continuity of Care Documents)
// =============================================================================

export async function createCCD(data) {
  try {
    console.log('Creating CCD via API:', data.patientId);
    const token = await getAuthToken();
    
    // Get the user's default facility ID if not provided
    let ccdData = { ...data };
    if (!ccdData.facilityId) {
      const state = store.getState();
      const user = state.auth.user;
      const userFacilities = state.facilities.userFacilities;
      const defaultFacility = getDefaultFacility(user, userFacilities);
      if (!defaultFacility || !defaultFacility.id) {
        throw new Error('No facility ID available. Please ensure you have access to a facility.');
      }
      ccdData.facilityId = defaultFacility.id;
    }
    
    const response = await axios.post(
      `${API_BASE}/clinical/ccds`,
      ccdData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      id: response.data.id,
      ...response.data
    };
  } catch (error) {
    console.error('Error creating CCD:', error);
    throw error;
  }
}

export async function getCCDsByPatient(patientId) {
  try {
    console.log('Getting CCDs by patient via API:', patientId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/clinical/ccds/patient/${patientId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.ccds;
  } catch (error) {
    console.error('Error getting CCDs by patient:', error);
    throw error;
  }
}

export async function getAllCCDs() {
  try {
    console.log('Getting all CCDs via API');
    const token = await getAuthToken();
    
    // Get the user's default facility ID
    const state = store.getState();
    const user = state.auth.user;
    const userFacilities = state.facilities.userFacilities;
    const defaultFacility = getDefaultFacility(user, userFacilities);
    if (!defaultFacility || !defaultFacility.id) {
      throw new Error('No facility ID available. Please ensure you have access to a facility.');
    }
    
    const response = await axios.get(
      `${API_BASE}/clinical/ccds?facilityId=${defaultFacility.id}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.ccds;
  } catch (error) {
    console.error('Error getting all CCDs:', error);
    throw error;
  }
}

// =============================================================================
// CARE TEAMS
// =============================================================================

export async function setCareTeam(patientId, members, updatedBy) {
  try {
    console.log('Setting care team via API:', patientId);
    const token = await getAuthToken();
    
    // Get the user's default facility ID
    const state = store.getState();
    const user = state.auth.user;
    const userFacilities = state.facilities.userFacilities;
    const defaultFacility = getDefaultFacility(user, userFacilities);
    if (!defaultFacility || !defaultFacility.id) {
      throw new Error('No facility ID available. Please ensure you have access to a facility.');
    }
    
    const response = await axios.post(
      `${API_BASE}/clinical/care-teams`,
      {
        patientId,
        members,
        facilityId: defaultFacility.id
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      id: response.data.id,
      ...response.data
    };
  } catch (error) {
    console.error('Error setting care team:', error);
    throw error;
  }
}

export async function updateCareTeam(teamId, updates) {
  try {
    console.log('Updating care team via API:', teamId);
    const token = await getAuthToken();
    
    const response = await axios.put(
      `${API_BASE}/clinical/care-teams/${teamId}`,
      updates,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      id: response.data.id,
      ...response.data
    };
  } catch (error) {
    console.error('Error updating care team:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

export async function deleteCareTeam(teamId) {
  try {
    console.log('Deleting care team via API:', teamId);
    const token = await getAuthToken();
    
    await axios.delete(
      `${API_BASE}/clinical/care-teams/${teamId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting care team:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

export async function getCareTeam(patientId) {
  try {
    console.log('Getting care team via API:', patientId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/clinical/care-teams/patient/${patientId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.careTeam;
  } catch (error) {
    console.error('Error getting care team:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function getAllCareTeams() {
  try {
    console.log('Getting all care teams via API');
    const token = await getAuthToken();
    
    // Get the user's default facility ID
    const state = store.getState();
    const user = state.auth.user;
    const userFacilities = state.facilities.userFacilities;
    const defaultFacility = getDefaultFacility(user, userFacilities);
    if (!defaultFacility || !defaultFacility.id) {
      console.warn('No facility ID available for getAllCareTeams, returning empty array');
      return [];
    }
    
    const response = await axios.get(
      `${API_BASE}/clinical/care-teams?facilityId=${defaultFacility.id}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.careTeams || [];
  } catch (error) {
    console.error('Error getting all care teams:', error);
    // Return empty array instead of throwing to prevent UI breaking
    return [];
  }
}

// =============================================================================
// CARE PLANS
// =============================================================================

export async function setCarePlan(patientId, title, description, goals) {
  try {
    console.log('Setting care plan via API:', patientId);
    const token = await getAuthToken();
    
    // Get the user's default facility ID
    const state = store.getState();
    const user = state.auth.user;
    const userFacilities = state.facilities.userFacilities;
    const defaultFacility = getDefaultFacility(user, userFacilities);
    if (!defaultFacility || !defaultFacility.id) {
      throw new Error('No facility ID available. Please ensure you have access to a facility.');
    }
    
    const response = await axios.post(
      `${API_BASE}/clinical/care-plans`,
      {
        patientId,
        title,
        description,
        goals,
        facilityId: defaultFacility.id
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      id: response.data.id,
      ...response.data
    };
  } catch (error) {
    console.error('Error setting care plan:', error);
    throw error;
  }
}

export async function getCarePlan(patientId) {
  try {
    console.log('Getting care plan via API:', patientId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/clinical/care-plans/patient/${patientId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.carePlan;
  } catch (error) {
    console.error('Error getting care plan:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function getAllCarePlans() {
  try {
    console.log('Getting all care plans via API');
    const token = await getAuthToken();
    
    // Get the user's default facility ID
    const state = store.getState();
    const user = state.auth.user;
    const userFacilities = state.facilities.userFacilities;
    const defaultFacility = getDefaultFacility(user, userFacilities);
    if (!defaultFacility || !defaultFacility.id) {
      console.warn('No facility ID available for getAllCarePlans, returning empty array');
      return [];
    }
    
    const response = await axios.get(
      `${API_BASE}/clinical/care-plans?facilityId=${defaultFacility.id}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.carePlans || [];
  } catch (error) {
    console.error('Error getting all care plans:', error);
    // Return empty array instead of throwing to prevent UI breaking
    return [];
  }
}

// =============================================================================
// DIRECT MESSAGES
// =============================================================================

export async function createDirectMessage(data) {
  try {
    console.log('Creating direct message via API');
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/internal/messages/direct`,
      data,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      id: response.data.id,
      ...response.data
    };
  } catch (error) {
    console.error('Error creating direct message:', error);
    throw error;
  }
}

export async function getDirectMessagesByUser(userId, direction = 'in') {
  try {
    console.log('Getting direct messages by user via API:', userId, direction);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('direction', direction);
    
    const response = await axios.get(
      `${API_BASE}/internal/messages/direct/user/${userId}?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.messages;
  } catch (error) {
    console.error('Error getting direct messages by user:', error);
    throw error;
  }
}

export async function getAllDirectMessages() {
  try {
    console.log('Getting all direct messages via API');
    const token = await getAuthToken();
    
    // Get the user's default facility ID
    const state = store.getState();
    const user = state.auth.user;
    const userFacilities = state.facilities.userFacilities;
    const defaultFacility = getDefaultFacility(user, userFacilities);
    if (!defaultFacility || !defaultFacility.id) {
      console.warn('No facility ID available for getAllDirectMessages');
      return [];
    }
    
    const response = await axios.get(
      `${API_BASE}/internal/messages/direct?facilityId=${defaultFacility.id}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.messages || [];
  } catch (error) {
    console.error('Error getting all direct messages:', error);
    // Return empty array instead of throwing to prevent UI breaking
    return [];
  }
}

// Quality metrics have been moved to biReportsService.js

// =============================================================================
// CARE GAPS (BigQuery Calculated - Dynamic based on patient data)
// =============================================================================

/**
 * Get care gaps calculated dynamically from BigQuery
 * Care gaps are never stored in Firestore - they are calculated from patient data,
 * encounters, and procedures in real-time via BigQuery views.
 */
export async function getCareGaps(filters = {}, facilityId = null) {
  try {
    console.log('Getting calculated care gaps from BigQuery:', filters);
    const token = await getAuthToken();
    
    // Use provided facilityId or fall back to Redux state
    let targetFacilityId = facilityId;
    if (!targetFacilityId) {
      const state = store.getState();
      const user = state.auth.user;
      const userFacilities = state.facilities.userFacilities;
      const defaultFacility = getDefaultFacility(user, userFacilities);
      targetFacilityId = defaultFacility?.id;
    }
    
    if (!targetFacilityId) {
      throw new Error('No facility ID available. Please ensure you have access to a facility.');
    }
    
    const params = new URLSearchParams();
    params.append('facilityId', targetFacilityId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && value !== 'All') {
        params.append(key, value.toString());
      }
    });
    
    // Use BigQuery analytics endpoint for calculated care gaps
    const response = await axios.get(
      `${API_BASE}/bi/analytics/care-gaps?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.careGaps || [];
  } catch (error) {
    console.error('Error getting calculated care gaps from BigQuery:', error);
    // Return empty array on error to prevent undefined errors in UI
    return [];
  }
}

/**
 * Get care gaps summary from BigQuery
 */
export async function getCareGapsSummary(facilityId = null) {
  try {
    console.log('Getting care gaps summary from BigQuery');
    const token = await getAuthToken();
    
    let targetFacilityId = facilityId;
    if (!targetFacilityId) {
      const state = store.getState();
      const user = state.auth.user;
      const userFacilities = state.facilities.userFacilities;
      const defaultFacility = getDefaultFacility(user, userFacilities);
      targetFacilityId = defaultFacility?.id;
    }
    
    if (!targetFacilityId) {
      throw new Error('No facility ID available.');
    }
    
    const response = await axios.get(
      `${API_BASE}/bi/analytics/care-gaps/summary?facilityId=${targetFacilityId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.summary || {};
  } catch (error) {
    console.error('Error getting care gaps summary:', error);
    return {};
  }
}

/**
 * Get care gaps breakdown by category from BigQuery
 */
export async function getCareGapsByCategory(facilityId = null) {
  try {
    console.log('Getting care gaps by category from BigQuery');
    const token = await getAuthToken();
    
    let targetFacilityId = facilityId;
    if (!targetFacilityId) {
      const state = store.getState();
      const user = state.auth.user;
      const userFacilities = state.facilities.userFacilities;
      const defaultFacility = getDefaultFacility(user, userFacilities);
      targetFacilityId = defaultFacility?.id;
    }
    
    if (!targetFacilityId) {
      throw new Error('No facility ID available.');
    }
    
    const response = await axios.get(
      `${API_BASE}/bi/analytics/care-gaps/by-category?facilityId=${targetFacilityId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.categories || [];
  } catch (error) {
    console.error('Error getting care gaps by category:', error);
    return [];
  }
}

/**
 * Get care gaps breakdown by provider from BigQuery
 */
export async function getCareGapsByProvider(facilityId = null) {
  try {
    console.log('Getting care gaps by provider from BigQuery');
    const token = await getAuthToken();
    
    let targetFacilityId = facilityId;
    if (!targetFacilityId) {
      const state = store.getState();
      const user = state.auth.user;
      const userFacilities = state.facilities.userFacilities;
      const defaultFacility = getDefaultFacility(user, userFacilities);
      targetFacilityId = defaultFacility?.id;
    }
    
    if (!targetFacilityId) {
      throw new Error('No facility ID available.');
    }
    
    const response = await axios.get(
      `${API_BASE}/bi/analytics/care-gaps/by-provider?facilityId=${targetFacilityId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.providers || [];
  } catch (error) {
    console.error('Error getting care gaps by provider:', error);
    return [];
  }
}

// =============================================================================
// INTERVENTIONS
// =============================================================================

export async function getInterventions(filters = {}, facilityId = null) {
  try {
    console.log('Getting interventions via API:', filters);
    const token = await getAuthToken();
    
    // Use provided facilityId or fall back to Redux state
    let targetFacilityId = facilityId;
    if (!targetFacilityId) {
      const state = store.getState();
      const user = state.auth.user;
      const userFacilities = state.facilities.userFacilities;
      const defaultFacility = getDefaultFacility(user, userFacilities);
      targetFacilityId = defaultFacility?.id;
    }
    
    if (!targetFacilityId) {
      throw new Error('No facility ID available. Please ensure you have access to a facility.');
    }
    
    const params = new URLSearchParams();
    params.append('facilityId', targetFacilityId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && value !== 'All') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/clinical/interventions?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.interventions || [];
  } catch (error) {
    console.error('Error getting interventions:', error);
    // Return empty array on error to prevent undefined errors in UI
    return [];
  }
}

// =============================================================================
// FILE UPLOAD HELPER
// =============================================================================

export async function uploadFile(file, path) {
  try {
    console.log('Uploading file via API:', path);
    const token = await getAuthToken();
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', path);
    
    const response = await axios.post(
      `${API_BASE}/internal/files/upload`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    
    return response.data.downloadURL;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
}

// =============================================================================
// PATIENT MANAGEMENT
// =============================================================================

export async function createOrUpdatePatient(patient) {
  try {
    console.log('Creating/updating patient via API:', patient.patientId);
    const token = await getAuthToken();
    
    const response = await axios.put(
      `${API_BASE}/patients/${patient.patientId}`,
      patient,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      id: response.data.id,
      ...response.data
    };
  } catch (error) {
    console.error('Error creating/updating patient:', error);
    throw error;
  }
}

export async function getAllPatients(facilityId = null) {
  try {
    console.log('Getting all patients via API');
    const token = await getAuthToken();
    
    // Use provided facilityId or get from store
    let targetFacilityId = facilityId;
    if (!targetFacilityId) {
      const state = store.getState();
      const user = state.auth.user;
      const userFacilities = state.facilities.userFacilities;
      const defaultFacility = getDefaultFacility(user, userFacilities);
      targetFacilityId = defaultFacility?.id;
    }
    
    if (!targetFacilityId) {
      throw new Error('No facility ID available. Please ensure you have access to a facility.');
    }
    
    const response = await axios.get(
      `${API_BASE}/clinical/facilities/${targetFacilityId}/patients`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.patients || [];
  } catch (error) {
    console.error('Error getting all patients:', error);
    throw error;
  }
}

// =============================================================================
// ADDITIONAL HELPER FUNCTIONS
// =============================================================================

/**
 * Search referrals by various criteria
 */
export async function searchReferrals(searchTerm, filters = {}) {
  try {
    console.log('Searching referrals via API:', searchTerm);
    const token = await getAuthToken();
    
    // Get the user's default facility ID
    const state = store.getState();
    const user = state.auth.user;
    const userFacilities = state.facilities.userFacilities;
    const defaultFacility = getDefaultFacility(user, userFacilities);
    if (!defaultFacility || !defaultFacility.id) {
      throw new Error('No facility ID available. Please ensure you have access to a facility.');
    }
    
    const params = new URLSearchParams();
    params.append('facilityId', defaultFacility.id);
    if (searchTerm) {
      params.append('search', searchTerm);
    }
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/clinical/referrals/search?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Handle both response formats for backward compatibility
    const referrals = response.data.referrals || response.data || [];
    
    // Ensure we always return an array
    if (!Array.isArray(referrals)) {
      console.warn('searchReferrals: Expected array but got:', typeof referrals, referrals);
      return [];
    }
    
    return referrals;
  } catch (error) {
    // If search endpoint doesn't exist, fallback to getting all and filtering
    try {
      const state = store.getState();
      const user = state.auth.user;
      const userFacilities = state.facilities.userFacilities;
      const defaultFacility = getDefaultFacility(user, userFacilities);
      const facilityId = defaultFacility?.id;
      const allReferrals = await getAllReferrals(facilityId);
      
      if (!searchTerm || searchTerm.trim() === '') {
        return allReferrals;
      }
      
      const lowerSearchTerm = searchTerm.toLowerCase();
      return allReferrals.filter(referral => 
        (referral.patientName && referral.patientName.toLowerCase().includes(lowerSearchTerm)) ||
        (referral.providerName && referral.providerName.toLowerCase().includes(lowerSearchTerm)) ||
        (referral.specialty && referral.specialty.toLowerCase().includes(lowerSearchTerm)) ||
        (referral.reason && referral.reason.toLowerCase().includes(lowerSearchTerm))
      );
    } catch (fallbackError) {
      console.error('Error searching referrals (fallback):', fallbackError);
      throw error;
    }
  }
}

/**
 * Get care coordination statistics
 */
export async function getCareCoordinationStats(facilityId = null, dateRange = {}) {
  // Use provided facilityId or get default facility
  let targetFacilityId = facilityId;
  if (!targetFacilityId) {
    const state = store.getState();
    const user = state.auth.user;
    const userFacilities = state.facilities.userFacilities;
    const defaultFacility = getDefaultFacility(user, userFacilities);
    if (!defaultFacility || !defaultFacility.id) {
      throw new Error('No facility ID available. Please ensure you have access to a facility.');
    }
    targetFacilityId = defaultFacility.id;
  }
  
  try {
    console.log('Getting care coordination statistics via API:', targetFacilityId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', targetFacilityId);
    if (dateRange.startDate) {
      params.append('startDate', dateRange.startDate);
    }
    if (dateRange.endDate) {
      params.append('endDate', dateRange.endDate);
    }
    
    const response = await axios.get(
      `${API_BASE}/clinical/care-coordination/stats?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.stats;
  } catch (error) {
    // If stats endpoint doesn't exist, calculate manually
    try {
      const [referrals, careTeams, carePlans] = await Promise.all([
        getAllReferrals(targetFacilityId),
        getAllCareTeams(),
        getAllCarePlans()
      ]);
      
      return {
        totalReferrals: referrals.length,
        totalCareTeams: careTeams.length,
        totalCarePlans: carePlans.length,
        referralsByStatus: referrals.reduce((acc, ref) => {
          const status = ref.status || 'pending';
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {}),
        period: dateRange
      };
    } catch (fallbackError) {
      console.error('Error getting care coordination stats (fallback):', fallbackError);
      throw error;
    }
  }
}

/**
 * Get active care plans for a facility
 */
export async function getActiveCareTeamsForFacility(facilityId) {
  try {
    const allCareTeams = await getAllCareTeams();
    return allCareTeams.filter(team => 
      team.facilityId === facilityId && 
      team.status !== 'inactive'
    );
  } catch (error) {
    console.error('Error getting active care teams for facility:', error);
    throw error;
  }
}

/**
 * Get patient care coordination summary
 */
export async function getPatientCareCoordinationSummary(patientId) {
  try {
    console.log('Getting patient care coordination summary via API:', patientId);
    
    const [referrals, careTeam, carePlan, ccds] = await Promise.all([
      getReferralsByPatient(patientId),
      getCareTeam(patientId),
      getCarePlan(patientId),
      getCCDsByPatient(patientId)
    ]);
    
    return {
      patientId,
      referrals: {
        total: referrals.length,
        active: referrals.filter(ref => ref.status === 'active' || ref.status === 'pending').length,
        completed: referrals.filter(ref => ref.status === 'completed').length,
        recent: referrals.slice(0, 5)
      },
      careTeam: careTeam ? {
        hasTeam: true,
        memberCount: careTeam.members ? careTeam.members.length : 0,
        lastUpdated: careTeam.updatedAt
      } : { hasTeam: false },
      carePlan: carePlan ? {
        hasPlan: true,
        itemCount: carePlan.items ? carePlan.items.length : 0,
        lastUpdated: carePlan.updatedAt
      } : { hasPlan: false },
      ccds: {
        total: ccds.length,
        recent: ccds.slice(0, 3)
      }
    };
  } catch (error) {
    console.error('Error getting patient care coordination summary:', error);
    throw error;
  }
}

/**
 * Bulk operations for care coordination
 */
export async function bulkUpdateReferralStatus(referralIds, newStatus, updatedBy, notes = '') {
  try {
    console.log('Bulk updating referral status via API:', referralIds.length, newStatus);
    
    const promises = referralIds.map(referralId => 
      updateReferral(referralId, { 
        status: newStatus, 
        statusNotes: notes,
        updatedBy
      })
    );
    
    const results = await Promise.allSettled(promises);
    
    const summary = {
      successful: 0,
      failed: 0,
      errors: []
    };
    
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        summary.successful++;
      } else {
        summary.failed++;
        summary.errors.push({
          referralId: referralIds[index],
          error: result.reason
        });
      }
    });
    
    return summary;
  } catch (error) {
    console.error('Error bulk updating referral status:', error);
    throw error;
  }
}
